import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { queryKeys } from "@/lib/query-keys";
import { Loader2 } from "lucide-react";

interface GroupCount {
  group_id: string;
  group_name: string;
  contact_count: number;
}

export function GroupNavMenu() {
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspaces();
  const workspaceId = currentWorkspace?.id ?? null;

  const { data: groups = [], isLoading } = useQuery<GroupCount[]>({
    queryKey: queryKeys.groups.nav(workspaceId ?? undefined),
    queryFn: async () => {
      if (!workspaceId) return [];
      const { data, error } = await supabase
        .from("view_group_counts")
        .select("group_id, group_name, contact_count")
        .eq("workspace_id", workspaceId);

      if (error) throw error;
      return (data ?? []) as GroupCount[];
    },
    enabled: !!workspaceId,
    staleTime: 30 * 1000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-3 px-2">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading...</span>
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="py-3 px-2 text-sm text-muted-foreground">
        No groups
      </div>
    );
  }

  return (
    <div className="min-w-[180px]">
      {groups.map((group) => (
        <div
          key={group.group_id}
          onClick={() => navigate(`/contacts?groupName=${encodeURIComponent(group.group_name)}`)}
          className="flex items-center justify-between text-sm py-1.5 px-2 rounded hover:bg-accent cursor-pointer"
        >
          <span>{group.group_name}</span>
          <span className="text-xs text-muted-foreground bg-muted rounded-full px-1.5">
            {group.contact_count}
          </span>
        </div>
      ))}
    </div>
  );
}
